import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { CustomsClearanceDetail } from '../models/customs-clearance.detail';
import { CustomsClearanceService } from '../models/customs-clearance-service.model';
import { CustomsClearanceServiceVariant } from '../models/customs-clearance-service-variant.model';
import { CustomsClearanceEndpointService } from '../clearance.service';
import {CustomsClearanceHeader} from "../models/customs-clearance-header.model";
import { DialogModule } from "primeng/dialog";
import { ProgressBarModule } from "primeng/progressbar";
import { SelectModule } from "primeng/select";
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { FieldsetModule } from "primeng/fieldset";
import { Company } from 'src/app/company/company.model';
import { ContractsService } from 'src/app/contracts/contracts.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { ProductsService } from 'src/app/order/product.service';
import { Products } from 'src/app/order/models/product.model';
import { ProductVariant } from 'src/app/order/models/product-variant.model';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-add-edit-clearance-charges',
    templateUrl: './add-edit-clearance-charges.component.html',
    styleUrls: ['./add-edit-clearance-charges.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ProgressBarModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    FieldsetModule
],
    providers: [
        MessageService,
        CustomsClearanceEndpointService,
        ContractsService,
        SessionStorageService
    ]
})
export class AddEditClearanceChargesComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() clearance: CustomsClearanceHeader;
    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() clearanceCharge: CustomsClearanceDetail | undefined;
    @Output() chargeSaved: EventEmitter<CustomsClearanceDetail> = new EventEmitter<CustomsClearanceDetail>();

    chargeForm!: FormGroup;
    editMode: boolean = false;
    isLoading: boolean = false;
    services: SelectItem<CustomsClearanceService>[] = [];
    variants: SelectItem<CustomsClearanceServiceVariant>[] = [];
    logisticsProducts: Products[] = [];
    companies: Company[] = [];
    readonly sourceModes = [
        { label: 'Catalog Service', value: 'PRODUCT' },
        { label: 'Manual / Legacy Service', value: 'MANUAL' }
    ];

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private clearanceService: CustomsClearanceEndpointService,
        private contractService: ContractsService,
        private sessionStorageService: SessionStorageService,
        private productsService: ProductsService
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        this.loadCompanies();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && changes['visible'].currentValue) {
            this.loadData();
            if (this.clearanceCharge) {
                this.editMode = true;
                this.chargeForm.patchValue(this.clearanceCharge);
            } else {
                this.clearanceCharge = new CustomsClearanceDetail();
                this.clearanceCharge.clearanceHeader = this.clearance;
                this.clearanceCharge.lineNumber = this.clearance.clearanceDetails.length + 1;
                this.chargeForm.patchValue(this.clearanceCharge);
            }
        }
    }

    loadData() {
        this.isLoading = true;
        const companyId = this.clearance?.owningCompanyId || this.sessionStorageService.getCompany()?.id || 0;
        forkJoin({
            services: this.clearanceService.getAllCustomsClearanceServices(),
            variants: this.clearanceService.getAllCustomsClearanceServiceVariants(),
            products: this.productsService.getLogisticsCatalog(companyId, 'CUSTOMS_CLEARANCE')
        }).subscribe({
            next: ({ services, variants, products }) => {
                this.services = services.map(service => ({ value: service, label: service.serviceName }));
                this.variants = variants.map(variant => ({ value: variant, label: variant.variantName }));
                this.logisticsProducts = products || [];
                const productId = this.clearanceCharge?.productId;
                this.chargeForm.patchValue({
                    sourceMode: productId ? 'PRODUCT' : 'MANUAL',
                    productId: productId || null,
                    productVariantId: this.clearanceCharge?.productVariantId || null,
                    service: this.services.find(s => s.value.serviceID === this.clearanceCharge?.service?.serviceID),
                    variant: this.variants.find(v => v.value.variantID === this.clearanceCharge?.variant?.variantID)
                }, { emitEvent: false });
                this.updateSourceValidation(productId ? 'PRODUCT' : 'MANUAL');
                this.isLoading = false;
            },
            error: () => {
                this.logisticsProducts = [];
                this.isLoading = false;
            }
        });
    }

    initForm() {
        this.chargeForm = this.fb.group({
            lineNumber: [{ value: null, disabled: true }, Validators.required],
            sourceMode: ['PRODUCT', Validators.required],
            productId: [null],
            productVariantId: [null],
            operationalCode: [null],
            service: [null, Validators.required],
            variant: [null],
            description: [''],
            quantity: [1, Validators.required],
            rate: [0, Validators.required],
            amount: [0, Validators.required],
            vat_percentage: [0],
            vat_amount: [0],
            totalAmount: [0],
            buyingAmount: [0],
            sellingAmount: [0],
            billTo: [null, Validators.required]
        });
        this.chargeForm.get('sourceMode')?.valueChanges.subscribe(mode => this.updateSourceValidation(mode));
        this.chargeForm.get('productId')?.valueChanges.subscribe(productId => this.applyProductDefaults(productId));
        this.chargeForm.get('quantity')?.valueChanges.subscribe(() => this.recalculateAmount());
        this.chargeForm.get('rate')?.valueChanges.subscribe(() => this.recalculateAmount());
        this.chargeForm.get('vat_percentage')?.valueChanges.subscribe(() => this.recalculateAmount());
    }

    loadCompanies() {
        this.contractService.getContractRelatedCompanies(this.sessionStorageService.getCompany()!.id).subscribe(
            value => {
                this.companies = value;
            }, error => {
                console.log(error);
                this.messageService.add({
                    severity: 'error', summary: 'Fetching Contract companies failed',
                    detail: 'Your order creation failed due to the following reason: ' + error.detail
                });
            }
        );
    }

    saveCharge() {
        if (this.chargeForm.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct all form errors.'
            });
            return;
        }
        this.isLoading = true;
        const { sourceMode, ...payload } = this.chargeForm.getRawValue();
        const charge = payload as CustomsClearanceDetail;
        charge.lineNumber = this.clearanceCharge?.lineNumber ?? this.clearance.clearanceDetails.length + 1;
        charge.clearanceHeader = this.clearance;
        charge.service = this.chargeForm.get('service')?.value?.value;
        charge.variant = this.chargeForm.get('variant')?.value?.value;
        if (this.chargeForm.get('sourceMode')?.value !== 'PRODUCT') {
            charge.productId = undefined;
            charge.productVariantId = undefined;
        }
        if (this.editMode) {
            charge.clearanceDetailID = this.clearanceCharge?.clearanceDetailID!;
            charge.createdDate = this.clearanceCharge?.createdDate!;
            charge.updatedDate = new Date();
            this.clearanceService.updateCustomsClearanceDetail(this.clearance.clearanceHeaderID!, charge.clearanceDetailID, charge).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Charge updated successfully.'
                    });
                    this.chargeSaved.emit(charge);
                    this.isLoading = false;
                    this.closeDialog();
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update charge.'
                    });
                    this.isLoading = false;
                }
            });
        } else {
            this.clearanceService.createCustomsClearanceDetail(this.clearance.clearanceHeaderID!, charge).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Charge created successfully.'
                    });
                    this.chargeSaved.emit(charge);
                    this.isLoading = false;
                    this.closeDialog();
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update charge.'
                    });
                    this.isLoading = false;
                }
            });
        }
    }

    closeDialog() {
        this.visibleChange.emit(false);
        this.chargeForm.reset();
        this.editMode = false;
    }

    productVariants(): ProductVariant[] {
        return this.selectedProduct()?.productVariants || [];
    }

    selectedProduct(): Products | undefined {
        return this.logisticsProducts.find(product => product.productID === this.chargeForm.get('productId')?.value);
    }

    private applyProductDefaults(productId?: number | null): void {
        if (!productId || this.chargeForm.get('sourceMode')?.value !== 'PRODUCT') {
            return;
        }
        const product = this.logisticsProducts.find(item => item.productID === productId);
        if (!product) {
            return;
        }
        const usage = product.logisticsUsages?.find(item => item.usageModule === 'CUSTOMS_CLEARANCE' && item.active !== false);
        const variants = product.productVariants || [];
        const variantId = usage?.defaultVariantId || (variants.length === 1 ? variants[0].variantID : null);
        const variant = variants.find(item => item.variantID === variantId);
        const buying = variant?.buyingRateAdjustment ?? product.buyingRate ?? product.standardRate ?? 0;
        const selling = variant?.sellingRateAdjustment ?? product.sellingRate ?? product.standardRate ?? 0;
        const service = this.services.find(option =>
            option.value.productId === product.productID
            || this.normalize(option.value.serviceName) === this.normalize(usage?.operationalCode || product.name));
        this.chargeForm.patchValue({
            productVariantId: variantId,
            operationalCode: usage?.operationalCode || null,
            service: service || null,
            description: product.description || product.name,
            rate: selling,
            buyingAmount: buying,
            sellingAmount: selling,
            vat_percentage: product.taxRate || 0
        }, { emitEvent: false });
        this.recalculateAmount();
    }

    private recalculateAmount(): void {
        const quantity = Number(this.chargeForm.get('quantity')?.value || 0);
        const rate = Number(this.chargeForm.get('rate')?.value || 0);
        const amount = quantity * rate;
        const taxRate = Number(this.chargeForm.get('vat_percentage')?.value || 0);
        const vatAmount = amount * taxRate / 100;
        this.chargeForm.patchValue({
            amount,
            sellingAmount: amount,
            vat_amount: vatAmount,
            totalAmount: amount + vatAmount
        }, { emitEvent: false });
    }

    private updateSourceValidation(mode: string): void {
        const productControl = this.chargeForm.get('productId');
        const serviceControl = this.chargeForm.get('service');
        if (mode === 'PRODUCT') {
            productControl?.setValidators(Validators.required);
            serviceControl?.clearValidators();
        } else {
            productControl?.clearValidators();
            serviceControl?.setValidators(Validators.required);
            this.chargeForm.patchValue({
                productId: null,
                productVariantId: null,
                operationalCode: null
            }, { emitEvent: false });
        }
        productControl?.updateValueAndValidity({ emitEvent: false });
        serviceControl?.updateValueAndValidity({ emitEvent: false });
    }

    private normalize(value?: string | null): string {
        return (value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
}
